<template>
    <article>
        <p>
            We've just released <a href="https://github.com/coroot/coroot" target="_blank">Coroot Community Edition</a> v0.5.
            This release introduces application-centric inspections that make troubleshooting distributed systems even easier and more efficient.
        </p>

        <p>
            Let’s dive into the details of how this differs from conventional dashboards and alerts.
        </p>

        <p>
            When you look at any dashboard, you are mentally evaluating whether the metrics are within their
            acceptable range of values or not.
            In order to automate this process, you can configure alerting rules to notify you when a metric exceeds a threshold.
            However, alerts usually have no context, so you have to manually extract issues relevant to a particular app
            from the alert stream.
        </p>

        <v-img eager max-width="600" class="ma-auto my-6" src="/static/img/docs/coroot-ce/alerting-rules.svg" />

        <p>
            For example, a contextless alerting
            <a href="https://awesome-prometheus-alerts.grep.to/rules.html#rule-host-and-hardware-1-13" target="_blank">rule</a>:
        </p>

        <pre v-hl.yaml>
- alert: HostHighCpuLoad
  expr: 100 - (avg by(instance) (rate(node_cpu_seconds_total{mode="idle"}[2m])) * 100) &gt; 80
  for: 0m
  labels:
    severity: warning
  annotations:
    summary: Host high CPU load (instance ((($labels.instance))))
    description: "CPU load is &gt; 80%"
        </pre>

        <p>
            This rule has no way to determine which applications are running on a particular node and annotate alerts with them.
            Therefore, if an application violates its SLOs (Service Level Objectives) due high CPU utilization of a related node,
            you will get two unrelated alerts and have to match them manually.
            Now imagine that several unrelated issues occur at once in your Kubernetes cluster of <var>100</var> nodes and <var>500</var> applications...
        </p>

        <p>
            <span class="font-weight-medium">That's exactly the problem Coroot is pretty good at solving!</span>
        </p>

        <h2>Application-centric dashboards on steroids</h2>

        <p>
            In order to get a clear picture of a distributed system, Coroot builds a model of it by:
        </p>
        <ul>
            <li>
                grouping individual containers into applications
            </li>
            <li>
                enriching each container representation with resource consumption statistics,
                subsystems statuses, logged errors and runtime-specific data
            </li>
            <li>
                linking applications to each other regarding their actual connections
            </li>
        </ul>

        <v-img max-width="600" class="ma-auto my-6" src="/static/img/docs/coroot-ce/coroot-app-inspections.svg" />

        <p>
            Using such a model allows Coroot to audit all subsystems within the context of a particular application.
            In other words, each inspection is aware of the instances, nodes, and upstream services related to any given app.
        </p>

        <p>
            Going back to the example, Coroot only checks CPU utilization of the nodes where the application is running.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/ce-cpu-inspection.png" />
        </v-card>

        <p>
            It doesn't matter how many nodes are in the cluster or how many applications are running in it,
            these supercharged dashboards allow you to focus on a particular app and only on the relevant nodes.
            Moreover, each dashboard has a status, so you can quickly check if there are any issues
            without having to open the corresponding dashboard.
        </p>

        <h2>Application status</h2>

        <p>
            There are two fundamentally different types of inspections:
        </p>

        <ul>
            <li>
                Symptom-based inspections which identify issues that affect users (<var>SLO</var>, <var>Postgres</var>, <var>Redis</var>).
            </li>
            <li>
                Supplementary inspections that help in cause analysis (<var>Instances</var>, <var>CPU</var>, <var>Memory</var>, <var>Storage</var>, <var>Network</var>, <var>Logs</var>).
            </li>
        </ul>

        <p>
            To highlight the status of each application on the overview page, Coroot takes into account only statuses
            of symptom-based inspections.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/docs/coroot-ce/app-status2.png" />
        </v-card>

        <p>
            You can easily configure the <var>Latency</var> and <var>Availability</var> SLOs for an application by using your custom Prometheus metrics:
        </p>

        <v-card max-width="600" class="pa-5 my-6 mx-auto">
            <v-img eager src="/static/img/docs/coroot-ce/slo-latency-config.png" />
        </v-card>

        <p>
            Instead of configuring custom SLOs for the Postgres and Redis databases, you can override the thresholds of the built-in checks.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/docs/coroot-ce/inspection-configs.png" />
        </v-card>

        <h2>Conclusion</h2>

        <p>
            As you can see, Coroot's approach to metric analysis is not rocket science, but it can greatly improve
            observability of any distributed system.
        </p>

        <p>
            Follow the instructions on our <router-link :to="{name: 'docs', params: {chapter: 'coroot', group: 'getting-started', item: 'installation'}}">Getting started</router-link> page to try Coroot now.
            If you like Coroot, give us a ⭐ on <a href="https://github.com/coroot/coroot" target="_blank">GitHub</a>️.
        </p>

        <p>Any questions or feedback? Reach out to us on <a href="https://join.slack.com/t/coroot-community/shared_invite/zt-1gsnfo0wj-I~Zvtx5CAAb8vr~r~vecyw" target="_blank"><v-icon>mdi-slack</v-icon>Slack</a>.</p>
    </article>
</template>

<script>

export default {
    components: {}
}
</script>

<style scoped>
</style>
